"use client"
import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import TestImage from "../../public/whale_marina_complex_3.avif";
import LogoSvg from "./svg/logo";
import PhoneFooterrSvg from "@/components/svg/phone-footer";
import MailFooterSvg from "@/components/svg/mail-footer";
import MapFooterSvg from "@/components/svg/map-footer";
import { useTranslations, useLocale  } from "next-intl";
import { routing } from "@/i18n/routing";
import { FaWhatsapp, FaTelegramPlane, FaLine } from "react-icons/fa";
import { logEvent } from '@/app/[locale]/googleAnalytics';

// Быстрые каналы связи (наименьшее трение для покупателя): WhatsApp/Telegram — один
// номер, что и телефон. Line пока заглушка (сделаем позже) — показываем как
// «скоро», без битой ссылки. Все внешние ссылки — noopener noreferrer.
const CONTACT_PHONE_INTL = "66930011220";
const MESSENGERS = [
  { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/${CONTACT_PHONE_INTL}`, Icon: FaWhatsapp },
  { key: "telegram", label: "Telegram", href: "https://t.me/Samila_Vladimir", Icon: FaTelegramPlane },
  { key: "line", label: "Line", href: null, Icon: FaLine }, // заглушка
];

const handleAppFooterClick = () => {
  logEvent('Кнопка контактов футер', {
    event_category: 'Social',
    event_label: 'WhatsApp and Telegram',
  });
};

export default function Footer() {
  const t = useTranslations("footer");
  const locale = useLocale();
  // localePrefix:"as-needed" → дефолтная локаль (en) на "/" без префикса. Якоря должны
  // быть "#about" для en и "/ru#about" для ru/th. Иначе "/en#about" → редирект /en→/
  // теряет якорь → ссылки футера не скроллят на английском. (Та же ловушка, что в шапке.)
  const base = locale === routing.defaultLocale ? "" : `/${locale}`;
  // Год копирайта вычисляется лениво при первом рендере клиентского компонента —
  // всегда актуальный без пересборки сайта (ленивый инициализатор useState не нарушает
  // правило set-state-in-effect).
  const [year] = useState(() => new Date().getFullYear());
  return (
    <footer id="">
      <div
        className="container flex justify-between p-0 md:flex-row flex-col"
        style={{ paddingTop: "104px" }}
      >
        <div className="flex flex-col justify-between gap-[20px] md:gap-0">
          <div className="flex flex-col">
            <h3 className="text-[30px] font-bold" style={{ lineHeight: "1" }}>
            {t("header")}
            </h3>
            <Link href={'tel:+66930011220'}
            onClick={handleAppFooterClick}>
            <div className="flex items-center gap-[10px] mt-[20px]">
           
              <PhoneFooterrSvg />
              <div className="flex flex-col">
                  +66 93 001 1220
              </div>
            </div>
            </Link>
            <Link href="mailto:sales@whalemarinacondo.com">
            <div className="flex items-center gap-[10px] mt-[25px]">
              <MailFooterSvg />
             sales@whalemarinacondo.com
            </div>
            </Link>
            <Link href={'https://maps.app.goo.gl/DPgfghhjuiRTXmsMA'} target="_blank" rel="noopener noreferrer" aria-label={t("mapAriaLabel")}>
            <div className="flex items-center gap-[10px] mt-[25px]">
              <MapFooterSvg />
                444, 360 Soi Na Jomtien 8, <br />
                Tambon Na Chom Thian
            </div>
            </Link>

            {/* Мессенджеры — быстрый канал связи. Кликзоны ≥44px (WCAG target-size). */}
            <div className="mt-[25px]">
              <p className="text-[12px] uppercase font-semibold mb-[12px]">{t("contactVia")}</p>
              <div className="flex items-center gap-[12px]">
                {MESSENGERS.map(({ key, label, href, Icon }) =>
                  href ? (
                    <Link
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      onClick={handleAppFooterClick}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#005F70] text-white hover:bg-[#017a99] transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </Link>
                  ) : (
                    // Заглушка «Line — скоро». aria-label на голом <span> недопустим
                    // (span без роли → ARIA игнорируется, audit «permitted ARIA attributes»
                    // фейлит). Даём role="img" — тогда aria-label легитимен и заглушка
                    // объявляется скринридеру как единый образ «Line — Coming soon».
                    // Внутренняя иконка aria-hidden, чтобы не дублировать озвучку.
                    <span
                      key={key}
                      role="img"
                      aria-label={`${label} — ${t("soon")}`}
                      title={t("soon")}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#005F70]/40 text-white/60 cursor-default"
                    >
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                  ),
                )}
              </div>
            </div>
          </div>
          <div className="w-full">
            <div className="absolute bg-[#005F70] left-0 h-[74px] w-[35%] hidden md:block"></div>
            <div className="footer-clip">
              <div className="relative">
                <p className="text-white relative pr-[38px]">
                  {t("decsr")} {t("secondRowDescr")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative md:ml-[-200px] ml-0 flex flex-col">
          {/* .footer-image фиксированного размера 534×362 (globals.css). Раньше был
              битый проп `fiil="true"` (опечатка fill) + не было sizes → оптимизатор
              отдавал крошечный w=256 на 534px-слот, картинка была мыльная. Даём явные
              width/height + sizes под реальную ширину + высокое качество. */}
          <Image
            src={TestImage}
            width={534}
            height={362}
            sizes="(max-width: 767px) 100vw, 534px"
            quality={80}
            className="footer-image"
            alt={t("complexImageAlt")}
            style={{ objectFit: "cover" }}
          />
          <div className="footer-m-clip"
            aria-hidden="true"
            role="presentation"
            data-nosnippet>
            <div className="bg-white rounded-full w-[69px] h-[69px] border-[#80808077] border-[1px] mt-[35px] ml-[20px] flex items-center justify-center md:hidden ">
              <LogoSvg />
            </div>
            <p className="text-white mt-[30px] text-[12px] ml-[20px] md:hidden">
              {t("decsr")} {t("secondRowDescr")}
            </p>
          </div>
          <div className="absolute bg-white top-[-27px] left-[-27px] w-[98px] h-[98px] rounded-full z-[2] md:flex items-center justify-center border-[#80808077] border-[1px] hidden">
            <LogoSvg />
          </div>
        </div>
      </div>
      <div className="container">
        <div className="flex justify-between lg:items-center items-start mt-10 pb-11 flex-col lg:flex-row">
          <div className="lg:mt-0 mt-[40px] uppercase">
          <Link
      href={`/${locale}/privacy`} target="_blank" rel="noopener noreferrer"
      className="text-[#017a99] hover:underline text-[12px]"
    >
      {t("privacyPolicy")}
    </Link>
            <p className="mt-4 text-[12px]">
              SK Pattaya Construction Development co. ltd{" "}
              <span className="text-[#017a99]">2013-{year}</span>
            </p>
          </div>

          <div className="lg:flex lg:flex-wrap grid grid-cols-2 gap-y-[20px] lg:gap-x-[25px] gap-x-[50px] -order-1 lg:order-1 mt-4 lg:mt-0 uppercase font-semibold">
            <Link href={`${base}/#about`}
            className="hover:underline">
              {t("about")}
            </Link>
            <Link href={`${base}/#flat-for-sale`}
            className="hover:underline">
              {t("buildingPlans")}
            </Link>
            <Link href={`${base}/#gallery`}

             className="hover:underline">
              {t("gallery")}
            </Link>
            <Link href={`${base}/#contacts`}
             className="hover:underline">
              {t("contacts")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
